import React from "react";
import { useNavigate } from "react-router-dom";
import logo from "../icons/logo.jpeg";

const forms = [
  {
    title: "Ops Guide (PDF)",
    description: "Read the Bellhopt Ops Guide and sign.",
  },
  {
    title: "Security & AUP",
    description: "VPN, device, and acceptable use policy.",
  },
  {
    title: "Quality Standards",
    description: "Quality, escalation, and ticket handling standards.",
  },
];

const resources = [
  {
    title: "Standards & Playbooks",
    items: [
      { label: "Support Playbook", href: "https://bellhoptllc.atlassian.net/wiki/spaces/GLOBOPS/pages/188776449/Ticket+Protocol+Library" },
      { label: "Quality Guidelines", href: "https://bellhoptllc.atlassian.net/wiki/spaces/GLOBOPS/pages/196608010/Compliance+Conduct+Standards" },
      { label: "Bellhopt Communication Guide", href: "https://bellhoptllc.atlassian.net/wiki/spaces/GLOBOPS/pages/196608010/Compliance+Conduct+Standards" },
    ],
  },
  {
    title: "Metrics & Dashboards",
    items: [
      { label: "Ticket Queue", href: "/tickets" },
      { label: "Dispatch Live Board", href: "/dispatch" },
      { label: "Announcements", href: "/announcements" },
    ],
  },
];

const quickLinks = [
  { label: "Ticket Queue", href: "/tickets" },
  { label: "Live Orders", href: "/orders/live" },
  { label: "Inquiries", href: "/inquiries" },
  // Dispatch is intentionally hidden until trained; see component logic
];

const OnboardingScreen = () => {
  const navigate = useNavigate();
  const adminInfo = JSON.parse(localStorage.getItem("adminInfo") || "{}");
  const trained = !!adminInfo?.trained;

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-white to-white">
      <div className="max-w-6xl mx-auto px-4 py-10 space-y-8">
        {/* Hero */}
        <div className="bg-white rounded-3xl shadow-lg overflow-hidden border border-indigo-100">
          <div className="flex flex-col lg:flex-row items-center gap-6 p-8 lg:p-10 bg-gradient-to-r from-indigo-50 via-white to-purple-50">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-white shadow flex items-center justify-center overflow-hidden">
                <img src={logo} alt="Bellhopt" className="object-contain w-14 h-14" />
              </div>
              <div>
                <p className="text-sm uppercase tracking-[0.15em] text-indigo-500 font-semibold">
                  Welcome to Bellhopt
                </p>
                <h1 className="text-3xl lg:text-4xl font-bold text-slate-900 leading-tight">
                  Your support onboarding hub
                </h1>
                <p className="text-sm text-slate-600 mt-2">
                  Get signed in, finish required forms, and review the standards you need for day one.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 ml-auto">
              <button
                onClick={() => navigate("/tickets")}
                className="px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold shadow hover:bg-indigo-700 transition-colors"
              >
                View ticket queue
              </button>
              {trained && (
                <button
                  onClick={() => navigate("/dispatch")}
                  className="px-4 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-semibold shadow hover:bg-slate-800 transition-colors"
                >
                  Open dispatch
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Action items */}
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">Step 1</p>
              <h2 className="text-xl font-bold text-slate-900">Review & sign required documents</h2>
              <p className="text-sm text-slate-600">Open the signing assistant to review PDFs, acknowledge, and sign in one flow.</p>
            </div>
            <button
              onClick={() => navigate("/signing/ops-guide")}
              className="px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold shadow hover:bg-indigo-700 transition-colors"
            >
              View & sign
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {forms.map((form) => (
              <div
                key={form.title}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex flex-col gap-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">{form.title}</h3>
                    <p className="text-sm text-slate-600">{form.description}</p>
                  </div>
                  <span className="px-2 py-1 rounded-lg bg-indigo-50 text-[11px] font-semibold text-indigo-700">
                    Required
                  </span>
                </div>
                <div className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500">
                  Organized automatically in signing assistant
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Reference library */}
        <section className="space-y-3">
          <div>
            <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">Step 2</p>
            <h2 className="text-xl font-bold text-slate-900">Reference standards & guidelines</h2>
            <p className="text-sm text-slate-600">Bookmark the playbooks, SOPs, and metrics you will use daily.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {resources.map((section) => (
              <div key={section.title} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-semibold text-slate-900">{section.title}</h3>
                  <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Start here</span>
                </div>
                <div className="flex flex-col gap-2">
                  {section.items.map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      target={item.href?.startsWith("http") ? "_blank" : "_self"}
                      rel="noreferrer"
                      className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 hover:border-indigo-200 hover:bg-indigo-50/60 transition-colors"
                    >
                      <span className="text-sm font-medium text-slate-800">{item.label}</span>
                      <span className="text-[11px] text-indigo-600 font-semibold">Open</span>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Quick links */}
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">Step 3</p>
              <h2 className="text-xl font-bold text-slate-900">Jump into tools</h2>
              <p className="text-sm text-slate-600">Direct links to the support surfaces you will monitor.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {quickLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => navigate(link.href)}
                className="rounded-2xl border border-indigo-100 bg-white px-3 py-3 text-sm font-semibold text-indigo-700 shadow-sm hover:border-indigo-200 hover:bg-indigo-50 transition-colors"
              >
                {link.label}
              </button>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default OnboardingScreen;

